// Adapted from https://github.com/Flet/prettier-bytes/
// Changing 1000 bytes to 1024, so we can keep uppercase KB vs kB
// ISC License (c) Dan Flettre https://github.com/Flet/prettier-bytes/blob/master/LICENSE
export function prettyBytes(num?: number): string {
  if (num == null || Number.isNaN(num)) return '';
  const neg = num < 0;
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  if (neg) {
    num = -num;
  }

  if (num < 1) {
    return `${(neg ? '-' : '') + num} B`;
  }

  const exponent = Math.min(
    Math.floor(Math.log(num) / Math.log(1024)),
    units.length - 1
  );
  num = Number(num / Math.pow(1024, exponent));
  const unit = units[exponent];

  if (num >= 10 || num % 1 === 0) {
    // Do not show decimals when the number is two-digit, or if the number has no
    // decimal component.
    return `${(neg ? '-' : '') + num.toFixed(0)} ${unit}`;
  }
  return `${(neg ? '-' : '') + num.toFixed(1)} ${unit}`;
}

const PLAN_GIB = 1073741824;

/**
 * Format plan / quota bytes the same way products are defined
 * (1000 GiB = 1 TB), so 4TB caps show as "4 TB" not "3.9 TB".
 */
export function prettyQuotaBytes(num?: number | null): string {
  if (num == null || Number.isNaN(num)) return '';

  const neg = num < 0;
  const bytes = Math.abs(num);

  if (bytes < 1) {
    return `${(neg ? '-' : '') + bytes} B`;
  }

  const units: {label: string; size: number}[] = [
    {label: 'TB', size: 1000 * PLAN_GIB},
    {label: 'GB', size: PLAN_GIB},
    {label: 'MB', size: PLAN_GIB / 1024},
    {label: 'KB', size: PLAN_GIB / 1024 / 1024},
    {label: 'B', size: 1},
  ];

  for (const unit of units) {
    if (bytes >= unit.size || unit.label === 'B') {
      const value = bytes / unit.size;
      let rounded: string;
      if (value >= 10 || Number.isInteger(value)) {
        rounded = value.toFixed(0);
      } else if (Math.abs(value - Math.round(value)) < 0.05) {
        rounded = String(Math.round(value));
      } else {
        rounded = value.toFixed(1);
      }
      return `${neg ? '-' : ''}${rounded} ${unit.label}`;
    }
  }

  return `${neg ? '-' : ''}${bytes} B`;
}
